import React, { useCallback, useRef, useState } from 'react'
import {
    Button,
    CircularProgress,
    Divider,
    IconButton,
    Modal,
    Paper,
    Slide,
    Typography,
    useTheme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined'
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined'
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined'
import { toast } from 'react-toastify'

import { Container } from '../../../components'
import { importAppFolioTenantDirectory } from '../../../hooks'
import { typographyStyle } from '../../../theme/typographyStyle'

const MAX_BYTES = 25 * 1024 * 1024
const BRAND = '#008C85'

const XLSX_ACCEPT = {
    accept:
        '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}

function isXlsxFile(file: File) {
    const nameOk = file.name.toLowerCase().endsWith('.xlsx')
    const mimeOk =
        file.type === '' ||
        file.type ===
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    return nameOk && mimeOk
}

function readUploadError(err: unknown): string {
    const e = err as {
        response?: { data?: { message?: string; detail?: string | string[] } }
        message?: string
    }
    const data = e.response?.data
    const detail = data?.detail
    if (typeof data?.message === 'string') return data.message
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail)) return detail.join(', ')
    return e.message || 'Upload failed.'
}

function formatFileSize(bytes: number) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface Props {
    open: boolean
    onClose: () => void
}

export const AppFolioTenantDirectoryImportModal = (props: Props) => {
    const { open, onClose } = props
    const theme = useTheme()
    const inputRef = useRef<HTMLInputElement>(null)
    const [file, setFile] = useState<File | null>(null)
    const [dragActive, setDragActive] = useState(false)
    const [submitting, setSubmitting] = useState(false)

    const reset = () => {
        setFile(null)
        setDragActive(false)
        if (inputRef.current) inputRef.current.value = ''
    }

    const close = () => {
        reset()
        onClose()
    }

    const pickFile = useCallback((f: File | null | undefined) => {
        if (!f) return
        if (!isXlsxFile(f)) {
            toast.error('Please choose an Excel file (.xlsx).')
            return
        }
        if (f.size > MAX_BYTES) {
            toast.error('File must be 25 MB or smaller.')
            return
        }
        setFile(f)
    }, [])

    const onDrop = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setDragActive(false)
        const dropped = e.dataTransfer.files?.[0]
        pickFile(dropped)
    }

    const submit = async () => {
        if (!file) {
            toast.error('Select an AppFolio Tenant Directory export (.xlsx).')
            return
        }
        setSubmitting(true)
        try {
            const result = await importAppFolioTenantDirectory(file)

            if (result.queued) {
                const tid = result.task_id
                toast.success(
                    tid
                        ? `${result.message} Task ID (support): ${tid}.`
                        : result.message,
                )
                close()
            } else {
                toast.warning(
                    result.message ?? 'Unexpected response from server.',
                )
            }
        } catch (err) {
            toast.error(readUploadError(err))
        } finally {
            setSubmitting(false)
        }
    }

    const dropSurfaceStyle: React.CSSProperties = {
        borderRadius: 12,
        padding: theme.spacing(4, 3),
        textAlign: 'center',
        cursor: submitting ? 'default' : 'pointer',
        outline: 'none',
        transition:
            'border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease',
        border: `2px dashed ${dragActive ? BRAND : theme.palette.divider}`,
        backgroundColor: dragActive
            ? 'rgba(0, 140, 133, 0.06)'
            : theme.palette.grey[50],
        boxShadow: dragActive ? `0 0 0 3px rgba(0, 140, 133, 0.12)` : 'none',
    }

    return (
        <Modal
            open={open}
            onClose={() => {
                if (!submitting) close()
            }}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: theme.spacing(2),
            }}
            BackdropProps={{
                style: { backgroundColor: 'rgba(0, 0, 0, 0.45)' },
            }}
        >
            <Slide direction="up" in={open}>
                <Paper
                    elevation={12}
                    style={{
                        width: 'min(440px, 100%)',
                        maxWidth: '100%',
                        outline: 'none',
                        borderRadius: 12,
                        overflow: 'hidden',
                    }}
                >
                    <Container
                        style={{
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: theme.spacing(2, 2.5),
                            background: `linear-gradient(135deg, ${BRAND}08 0%, ${theme.palette.grey[50]} 55%, #fff 100%)`,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Typography
                            variant="h6"
                            style={{
                                ...typographyStyle(theme.typography.h6),
                                fontWeight: 700,
                                color: theme.palette.text.primary,
                                letterSpacing: '-0.02em',
                            }}
                        >
                            AppFolio tenant directory
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={close}
                            disabled={submitting}
                            aria-label="Close"
                            style={{ marginRight: -theme.spacing(0.5) }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Container>

                    <div style={{ padding: theme.spacing(2.5, 2.5, 2) }}>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{
                                lineHeight: 1.55,
                                marginBottom: theme.spacing(2),
                            }}
                        >
                            Upload your{' '}
                            <strong>AppFolio Tenant Directory</strong> export as
                            an Excel workbook. Processing runs in the
                            background—leases in this screen will not refresh
                            right away.
                        </Typography>

                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: theme.spacing(1.5),
                                padding: theme.spacing(1.5, 1.75),
                                marginBottom: theme.spacing(2),
                                borderRadius: 8,
                                backgroundColor: 'rgba(0, 140, 133, 0.07)',
                                border: `1px solid rgba(0, 140, 133, 0.18)`,
                            }}
                        >
                            <EmailOutlinedIcon
                                style={{
                                    color: BRAND,
                                    fontSize: 22,
                                    marginTop: 2,
                                    flexShrink: 0,
                                }}
                            />
                            <Typography
                                variant="body2"
                                style={{
                                    color: theme.palette.text.secondary,
                                    fontSize: 13,
                                    lineHeight: 1.5,
                                }}
                            >
                                When the import finishes, you&apos;ll get an
                                email whether it succeeded or failed—no need to
                                wait on this page.
                            </Typography>
                        </div>

                        <input
                            ref={inputRef}
                            type="file"
                            hidden
                            {...XLSX_ACCEPT}
                            onChange={(e) => pickFile(e.target.files?.[0])}
                        />

                        <div
                            role="button"
                            tabIndex={submitting ? -1 : 0}
                            onKeyDown={(e) => {
                                if (submitting) return
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    inputRef.current?.click()
                                }
                            }}
                            onClick={() =>
                                !submitting && inputRef.current?.click()
                            }
                            onDragEnter={(e) => {
                                e.preventDefault()
                                if (!submitting) setDragActive(true)
                            }}
                            onDragLeave={(e) => {
                                e.preventDefault()
                                setDragActive(false)
                            }}
                            onDragOver={(e) => {
                                e.preventDefault()
                                e.dataTransfer.dropEffect = 'copy'
                            }}
                            onDrop={submitting ? undefined : onDrop}
                            style={{
                                ...dropSurfaceStyle,
                                opacity: submitting ? 0.65 : 1,
                                pointerEvents: submitting ? 'none' : undefined,
                            }}
                        >
                            {file ? (
                                <>
                                    <InsertDriveFileOutlinedIcon
                                        style={{
                                            fontSize: 40,
                                            color: BRAND,
                                            marginBottom: theme.spacing(1),
                                        }}
                                    />
                                    <Typography
                                        variant="subtitle1"
                                        style={{
                                            fontWeight: 600,
                                            wordBreak: 'break-word',
                                            padding: theme.spacing(0, 1),
                                        }}
                                    >
                                        {file.name}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        style={{
                                            display: 'block',
                                            marginTop: theme.spacing(0.75),
                                        }}
                                    >
                                        {formatFileSize(file.size)} · .xlsx
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        style={{
                                            display: 'block',
                                            marginTop: theme.spacing(1.5),
                                            color: BRAND,
                                            fontWeight: 600,
                                        }}
                                    >
                                        Click to replace file
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <CloudUploadOutlinedIcon
                                        style={{
                                            fontSize: 48,
                                            color: dragActive
                                                ? BRAND
                                                : theme.palette.text.disabled,
                                            marginBottom: theme.spacing(1.5),
                                            transition: 'color 0.2s ease',
                                        }}
                                    />
                                    <Typography
                                        variant="subtitle1"
                                        style={{
                                            fontWeight: 600,
                                            marginBottom: theme.spacing(0.5),
                                        }}
                                    >
                                        Drop your file here
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        style={{
                                            marginBottom: theme.spacing(1),
                                        }}
                                    >
                                        or click to browse —{' '}
                                        <strong>.xlsx</strong> only
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                    >
                                        Maximum file size 25 MB
                                    </Typography>
                                </>
                            )}
                        </div>
                    </div>

                    <Divider />

                    <Container
                        style={{
                            padding: theme.spacing(2, 2.5),
                            justifyContent: 'flex-end',
                            gap: theme.spacing(1.5),
                            backgroundColor: theme.palette.grey[50],
                        }}
                    >
                        <Button
                            onClick={close}
                            disabled={submitting}
                            style={{ textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            disableElevation
                            onClick={submit}
                            disabled={submitting || !file}
                            style={{
                                textTransform: 'none',
                                minWidth: 168,
                                backgroundColor: BRAND,
                                color: '#fff',
                                fontWeight: 600,
                                borderRadius: 8,
                                padding: theme.spacing(1, 2),
                            }}
                        >
                            {submitting ? (
                                <CircularProgress size={22} color="inherit" />
                            ) : (
                                'Queue import'
                            )}
                        </Button>
                    </Container>
                </Paper>
            </Slide>
        </Modal>
    )
}
